
import { WorkspaceService } from "@/gen/omnios/v1/workspace_connect.js";
import { transport } from "@/lib/grpc";
import { type Language, useLocaleStore } from "@/lib/locale";
import { type Theme, useThemeStore } from "@/lib/theme";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useWorkspaceStore } from "@/lib/workspace";
import { Struct } from "@bufbuild/protobuf";
import { createClient } from "@connectrpc/connect";
import { createFileRoute } from "@tanstack/react-router";
import { Check, Globe, Monitor, Moon, Sun } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

const wsClient = createClient(WorkspaceService, transport);

export const Route = createFileRoute("/settings/appearance")({
	component: AppearancePage,
});

function AppearancePage() {
	const { t } = useTranslation("settings");
	const { theme, setTheme } = useThemeStore();
	const { language, setLanguage } = useLocaleStore();
	const { workspaces, selectedId } = useWorkspaceStore();
	const currentWs = workspaces.find((w) => w.id === selectedId);

	useBreadcrumbs([
		{ label: t("breadcrumb_settings"), href: "/settings" },
		{ label: t("appearance_breadcrumb") },
	]);

	const THEME_OPTIONS: {
		value: Theme;
		label: string;
		description: string;
		icon: LucideIcon;
	}[] = [
		{
			value: "system",
			label: t("appearance_system"),
			description: t("appearance_system_desc"),
			icon: Monitor,
		},
		{
			value: "light",
			label: t("appearance_light"),
			description: t("appearance_light_desc"),
			icon: Sun,
		},
		{
			value: "dark",
			label: t("appearance_dark"),
			description: t("appearance_dark_desc"),
			icon: Moon,
		},
	];

	const LANG_OPTIONS: {
		value: Language;
		label: string;
		description: string;
	}[] = [
		{
			value: "ru",
			label: "Русский",
			description: t("lang_ru_desc"),
		},
		{
			value: "en",
			label: "English",
			description: t("lang_en_desc"),
		},
	];

	async function handleLanguageChange(lang: Language) {
		setLanguage(lang);
		// Persist to workspace settings
		if (selectedId && currentWs) {
			const merged = { ...currentWs.settings, locale: lang };
			useWorkspaceStore.setState((s) => ({
				workspaces: s.workspaces.map((w) =>
					w.id === selectedId ? { ...w, settings: merged } : w,
				),
			}));
			try {
				await wsClient.updateWorkspace({
					id: selectedId,
					settings: Struct.fromJson(
						merged as Parameters<typeof Struct.fromJson>[0],
					),
				});
			} catch (e) {
				console.error("Failed to persist locale", e);
			}
		}
	}

	return (
		<div className="space-y-8">
			{/* Theme */}
			<div className="space-y-3">
				<h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
					{t("appearance_theme_section")}
				</h3>
				<div className="space-y-2 max-w-md">
					{THEME_OPTIONS.map((opt) => {
						const active = theme === opt.value;
						const Icon = opt.icon;
						return (
							<Button
								key={opt.value}
								variant="outline"
								onClick={() => setTheme(opt.value)}
								className={cn(
									"flex w-full items-center gap-3 h-auto px-4 py-3 text-left justify-start",
									active
										? "border-primary bg-primary/5"
										: "border-border hover:bg-accent",
								)}
							>
								<Icon className="h-5 w-5 shrink-0 text-muted-foreground" />
								<div className="flex-1">
									<p className="text-sm font-medium">{opt.label}</p>
									<p className="text-xs text-muted-foreground">
										{opt.description}
									</p>
								</div>
								{active && <Check className="h-4 w-4 shrink-0 text-primary" />}
							</Button>
						);
					})}
				</div>
			</div>

			{/* Language */}
			<div className="space-y-3">
				<h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
					{t("appearance_lang_section")}
				</h3>
				<div className="space-y-2 max-w-md">
					{LANG_OPTIONS.map((opt) => {
						const active = language === opt.value;
						return (
							<Button
								key={opt.value}
								variant="outline"
								onClick={() => handleLanguageChange(opt.value)}
								className={cn(
									"flex w-full items-center gap-3 h-auto px-4 py-3 text-left justify-start",
									active
										? "border-primary bg-primary/5"
										: "border-border hover:bg-accent",
								)}
							>
								<Globe className="h-5 w-5 shrink-0 text-muted-foreground" />
								<div className="flex-1">
									<p className="text-sm font-medium">{opt.label}</p>
									<p className="text-xs text-muted-foreground">
										{opt.description}
									</p>
								</div>
								{active && <Check className="h-4 w-4 shrink-0 text-primary" />}
							</Button>
						);
					})}
				</div>
			</div>
		</div>
	);
}
